import { useCallback, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { addVersion, removeVersion, setActiveVersion, updateVersion } from '../../../redux/voice-bot/prompt.slice';
import { v4 as uuidv4 } from 'uuid';
import { UI_CONFIG } from '../constants';

export const usePromptVersions = () => {
    const dispatch = useAppDispatch();
    const { versions, activeVersionId } = useAppSelector((state) => state.prompt);
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

    const handleAddVersion = useCallback(() => {
        const id = uuidv4();
        const newVersion = { id, label: `Version ${versions.length + 1}`, content: '' };
        dispatch(addVersion(newVersion));
        dispatch(setActiveVersion(id));
    }, [dispatch, versions.length]);

    const handleRemoveVersion = useCallback(
        (id: string) => {
            dispatch(removeVersion(id));
        },
        [dispatch]
    );

    const handleChangeTab = useCallback(
        (newValue: string) => {
            dispatch(setActiveVersion(newValue));
        },
        [dispatch]
    );

    const handleLabelSave = useCallback(
        (editingLabelId: string, newLabel: string) => {
            if (editingLabelId) {
                const updatedVersion = versions.find((v) => v.id === editingLabelId);
                if (updatedVersion) {
                    dispatch(updateVersion({ ...updatedVersion, label: newLabel }));
                }
            }
        },
        [dispatch, versions]
    );

    const debouncedUpdate = useCallback(
        (content: string) => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }

            debounceTimeout.current = setTimeout(() => {
                let updatedVersion = null;
                if (activeVersionId) {
                    updatedVersion = versions.find((v) => v.id === activeVersionId);
                }
                dispatch(
                    updateVersion({
                        id: activeVersionId || uuidv4(),
                        label: updatedVersion?.label || 'Saved Version',
                        content,
                    })
                );
            }, UI_CONFIG.DEBOUNCE_DELAY);
        },
        [activeVersionId, versions, dispatch]
    );

    useEffect(() => {
        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, []);

    return {
        versions,
        activeVersionId,
        handleAddVersion,
        handleRemoveVersion,
        handleChangeTab,
        handleLabelSave,
        debouncedUpdate,
    };
};
